import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Wrap,
  TopBar,
  LeftArea,
  RightArea,
  Input,
  DateInputEl,
  LabelsPane,
  DividerV,
  HintBar,
  Pill,
  Button,
  MenuWrap,
  MenuPanel,
  MenuItem,
  MenuBtn,
} from "./styles";

// ---------- Theme ----------
const THEME = {
  bg: "#ffffff",
  axis: "#e5e7eb",
  grid: "#eef2f7",
  text: "#0f172a",
  text2: "#334155",
  hint: "#64748b",
  zebraUser: "#f3f6fb",
  zebraA: "#ffffff",
  zebraB: "#f9fbfd",
  now: "#ef4444",
  barShadow: "rgba(2,6,23,.14)",
  brush: "rgba(59,130,246,.15)",
  brushBorder: "rgba(59,130,246,.9)",
};

const PALETTE = [
  { top: "#10b981", bot: "#059669", border: "#047857" },
  { top: "#38bdf8", bot: "#0ea5e9", border: "#0284c7" },
  { top: "#f59e0b", bot: "#d97706", border: "#b45309" },
  { top: "#a78bfa", bot: "#7c3aed", border: "#5b21b6" },
  { top: "#fb7185", bot: "#f43f5e", border: "#be123c" },
  { top: "#34d399", bot: "#10b981", border: "#059669" },
  { top: "#60a5fa", bot: "#3b82f6", border: "#1d4ed8" },
  { top: "#a3e635", bot: "#65a30d", border: "#3f6212" },
];

// ---------- Defaults ----------
const DEFAULT_START_HOUR = 9;
const DEFAULT_END_HOUR = 21;

// ---------- Utils ----------
const pad2 = (n: number) => (n < 10 ? `0${n}` : String(n));
const fmtHMst = (ms: number) => {
  const d = new Date(ms);
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(
    d.getSeconds()
  )}`;
};
const fmtDur = (sSec: number, eSec: number) => {
  const diff = Math.max(0, eSec - sSec);
  const h = Math.floor(diff / 3600);
  const m = Math.floor((diff % 3600) / 60);
  const s = Math.floor(diff % 60);
  return `${pad2(h)}:${pad2(m)}:${pad2(s)}`;
};
const startOfDay = (ms: number) => {
  const d = new Date(ms);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
};
const atHour = (ms: number, hour: number) => {
  const d = new Date(ms);
  d.setHours(hour, 0, 0, 0);
  return d.getTime();
};
const dateToInput = (ms: number) => {
  const d = new Date(ms);
  const y = d.getFullYear();
  const m = (d.getMonth() + 1).toString().padStart(2, "0");
  const dd = d.getDate().toString().padStart(2, "0");
  return `${y}-${m}-${dd}`;
};
function parseDateInput(s?: string | null, end = false): number | null {
  if (!s) return null;
  const m = /^([0-9]{4})-([0-9]{2})-([0-9]{2})$/.exec(s);
  if (!m) return null;
  const d = new Date(
    Number(m[1]),
    Number(m[2]) - 1,
    Number(m[3]),
    end ? 23 : 0,
    end ? 59 : 0,
    end ? 59 : 0,
    end ? 999 : 0
  );
  return d.getTime();
}

const roundRect = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
  fill: string | CanvasGradient,
  stroke: string
) => {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.lineTo(x + w - rr, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + rr);
  ctx.lineTo(x + w, y + h - rr);
  ctx.quadraticCurveTo(x + w, y + h, x + w - rr, y + h);
  ctx.lineTo(x + rr, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - rr);
  ctx.lineTo(x, y + rr);
  ctx.quadraticCurveTo(x, y, x + rr, y);
  ctx.closePath();
  ctx.fillStyle = fill;
  ctx.fill();
  ctx.strokeStyle = stroke;
  ctx.lineWidth = 1;
  ctx.stroke();
};

// ---------- Time step picker ----------
const pickTimeStep = (spanMs: number, targetPx: number) => {
  const candidates = [
    60e3,
    5 * 60e3,
    10 * 60e3,
    15 * 60e3,
    30 * 60e3,
    3600e3,
    3 * 3600e3,
    6 * 3600e3,
    12 * 3600e3,
    24 * 3600e3,
    2 * 24 * 3600e3,
    7 * 24 * 3600e3,
  ];
  const pxPerMs =
    Math.max(1, (window?.innerWidth || 1200) - 320) / Math.max(1, spanMs);
  let step = candidates[0];
  for (const c of candidates) {
    step = c;
    if (c * pxPerMs >= targetPx) break;
  }

  const dayMs = 24 * 3600e3;
  const weekMs = 7 * dayMs;
  const showYear = spanMs >= 180 * dayMs;

  const fmt = (d: Date) => {
    if (step >= weekMs) {
      return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(
        d.getDate()
      )}`;
    }
    if (step >= dayMs) {
      const base = `${d.getMonth() + 1}/${d.getDate()}`;
      return showYear ? `${d.getFullYear()}/${base}` : base;
    }
    if (step >= 3600e3) return `${pad2(d.getHours())}:00`;
    return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
  };

  return { stepMs: step, fmt, stepIsDayOrMore: step >= dayMs };
};

// ---------- Data shaping ----------
export type ValueTuple = [number, number | string | null | undefined];
export type Row = { instance: string; seconds?: number; values: ValueTuple[] };
export type Block = { user: string; data: Row[] };

const calculateMinGapSec = (tuples: ValueTuple[]) => {
  if (!tuples?.length) return Infinity;
  const times = tuples
    .map((v) => v?.[0] ?? 0)
    .filter((n) => Number.isFinite(n))
    .sort((a, b) => a - b);
  let minGap = Infinity;
  for (let i = 1; i < times.length; i++) {
    const d = times[i] - times[i - 1];
    if (d > 0 && d < minGap) minGap = d;
  }
  return minGap;
};

const toRuns = (values: ValueTuple[], gapSec: number, mergeGapSec = 5 * 60) => {
  if (!values?.length) return [] as { s: number; e: number; samples: number }[];

  const times = values
    .map((v) => v?.[0] ?? 0)
    .filter((n) => Number.isFinite(n))
    .sort((a, b) => a - b);

  if (!times.length) return [];

  const baseTol = Number.isFinite(gapSec) ? Math.round(gapSec * 1.5) : 0;
  const tol = Math.max(1, baseTol, mergeGapSec);

  const out: { s: number; e: number; samples: number }[] = [];
  let s = times[0];
  let samples = 1;

  for (let i = 1; i < times.length; i++) {
    const d = times[i] - times[i - 1];
    if (d <= tol) samples += 1;
    else {
      out.push({ s, e: times[i - 1], samples });
      s = times[i];
      samples = 1;
    }
  }

  out.push({ s, e: times[times.length - 1], samples });
  return out;
};

// ---------- Component ----------
export interface GanttTimelineCanvasProps {
  blocks: Block[];
  labelWidth: number;
  rowHeight: number;
  zebra?: boolean;
  onApplyDateRange?: (startISO: string, endISO: string) => void;
}

const GanttTimelineCanvas: React.FC<GanttTimelineCanvasProps> = ({
  blocks,
  labelWidth,
  rowHeight,
  zebra = true,
  onApplyDateRange,
}) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [query, setQuery] = useState("");
  const [selectMode, setSelectMode] = useState(false);
  const [brush, setBrush] = useState<null | { x0: number; x1: number }>(null);
  const [menuOpen, setMenuOpen] = useState(false);

  // Filter
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return blocks;
    return blocks
      .map((b) => ({
        ...b,
        data: b.data.filter(
          (r) =>
            r.instance.toLowerCase().includes(q) ||
            b.user.toLowerCase().includes(q)
        ),
      }))
      .filter((b) => b.data.length);
  }, [blocks, query]);

  // Flatten & layout
  type FlatItem = {
    y: number;
    user: string;
    instance: string;
    sMs: number;
    eMs: number;
    samples: number;
    palIdx: number;
  };

  const { items, yLabels, yIsHeader } = useMemo(() => {
    const labels: string[] = [];
    const isHeader: boolean[] = [];
    const items: FlatItem[] = [];

    let y = 0;
    let nMin = Infinity;
    let nMax = -Infinity;
    let pal = 0;

    filtered.forEach((b) => {
      labels.push(`👤 ${b.user}`);
      isHeader.push(true);
      y += 1;
      if (collapsed[b.user]) return;

      b.data.forEach((row) => {
        labels.push(`  └ ${row.instance}`);
        isHeader.push(false);

        const gapSec = calculateMinGapSec(row.values);
        const runs = toRuns(row.values, gapSec);
        runs.forEach((r) => {
          const sMs = r.s * 1000;
          const eMs = r.e * 1000;
          items.push({
            y,
            user: b.user,
            instance: row.instance,
            sMs,
            eMs,
            samples: r.samples,
            palIdx: pal,
          });
          nMin = Math.min(nMin, sMs);
          nMax = Math.max(nMax, eMs);
        });
        pal = (pal + 1) % PALETTE.length;
        y += 1;
      });
    });

    if (!Number.isFinite(nMin) || !Number.isFinite(nMax)) {
      const now = Date.now();
      nMin = now - 3600_000;
      nMax = now;
    }
    return { items, yLabels: labels, yIsHeader: isHeader };
  }, [filtered, collapsed]);

  // canvas 높이
  const computedHeight = useMemo(() => {
    const rows = yLabels.length || 1;
    return Math.max(700, rows * rowHeight + 8);
  }, [yLabels.length, rowHeight]);

  // 초기 범위: 오늘 9~21시
  const initRange = useMemo(() => {
    const today = Date.now();
    return {
      s: atHour(today, DEFAULT_START_HOUR),
      e: atHour(today, DEFAULT_END_HOUR),
    };
  }, []);

  const [xMin, setXMin] = useState(initRange.s);
  const [xMax, setXMax] = useState(initRange.e);

  // 날짜 입력(좌측에 배치)
  const [startStr, setStartStr] = useState(dateToInput(xMin));
  const [endStr, setEndStr] = useState(dateToInput(xMax));
  useEffect(() => {
    setStartStr(dateToInput(xMin));
    setEndStr(dateToInput(xMax));
  }, [xMin, xMax]);

  const applyRange = () => {
    const sDay = parseDateInput(startStr);
    const eDay = parseDateInput(endStr, true);
    if (sDay && eDay && eDay > sDay) {
      const s = atHour(sDay, DEFAULT_START_HOUR);
      const e = atHour(eDay, DEFAULT_END_HOUR);
      setXMin(s);
      setXMax(e);
      onApplyDateRange?.(dateToInput(s), dateToInput(e));
    }
  };

  // Drag / Hover
  const dragging = useRef(false);
  const dragStartX = useRef(0);
  const dragStartMin = useRef(0);
  const dragStartMax = useRef(0);

  const [hover, setHover] = useState<null | {
    x: number;
    y: number;
    item?: FlatItem;
  }>(null);

  // draw
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;

    const cssWidth = Math.max(
      1,
      (wrapRef.current?.clientWidth || 1200) - labelWidth
    );
    const cssHeight = computedHeight;

    canvas.width = Math.max(1, Math.floor(cssWidth * dpr));
    canvas.height = Math.max(1, Math.floor(cssHeight * dpr));
    canvas.style.width = `${cssWidth}px`;
    canvas.style.height = `${cssHeight}px`;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    // bg
    ctx.fillStyle = THEME.bg;
    ctx.fillRect(0, 0, cssWidth, cssHeight);

    // zebra rows
    for (let i = 0; i < yLabels.length; i++) {
      const y = i * rowHeight;
      ctx.fillStyle = zebra
        ? yIsHeader[i]
          ? THEME.zebraUser
          : i % 2
          ? THEME.zebraA
          : THEME.zebraB
        : THEME.bg;
      ctx.fillRect(0, y, cssWidth, rowHeight);
    }

    // grid
    const span = xMax - xMin;
    const targetPx = 120;
    const { stepMs, fmt, stepIsDayOrMore } = pickTimeStep(span, targetPx);
    const firstTick = Math.ceil(xMin / stepMs) * stepMs;

    if (!stepIsDayOrMore) {
      ctx.strokeStyle = THEME.grid;
      ctx.lineWidth = 1;
      ctx.beginPath();
      for (let t = firstTick; t <= xMax; t += stepMs) {
        const x = ((t - xMin) / span) * cssWidth;
        ctx.moveTo(x + 0.5, 0);
        ctx.lineTo(x + 0.5, cssHeight);
      }
      ctx.stroke();
    }

    // labels
    ctx.fillStyle = THEME.hint;
    ctx.font = "12px system-ui, -apple-system, Segoe UI, Roboto";

    if (stepIsDayOrMore) {
      ctx.strokeStyle = "#e2e8f0";
      ctx.lineWidth = 1.5;
      for (let t = firstTick; t <= xMax; t += stepMs) {
        const x = ((t - xMin) / span) * cssWidth;
        ctx.beginPath();
        ctx.moveTo(x + 0.5, 0);
        ctx.lineTo(x + 0.5, cssHeight);
        ctx.stroke();
        ctx.fillText(fmt(new Date(t)), x + 4, 14);
      }
    } else {
      for (let t = firstTick; t <= xMax; t += stepMs) {
        const x = ((t - xMin) / span) * cssWidth;
        ctx.fillText(fmt(new Date(t)), x + 4, 24);
      }
      const dayMs = 24 * 3600e3;
      let dayT = startOfDay(xMin + dayMs);
      ctx.strokeStyle = "#e2e8f0";
      ctx.lineWidth = 1.5;
      for (; dayT <= xMax; dayT += dayMs) {
        const x = ((dayT - xMin) / span) * cssWidth;
        ctx.beginPath();
        ctx.moveTo(x + 0.5, 0);
        ctx.lineTo(x + 0.5, cssHeight);
        ctx.stroke();
        const d = new Date(dayT);
        const label = `${d.getMonth() + 1}/${d.getDate()}`;
        ctx.fillText(label, x + 4, 12);
      }
    }

    // 09~21 가이드
    const today = startOfDay(Date.now());
    const nine = atHour(today, DEFAULT_START_HOUR);
    const twentyOne = atHour(today, DEFAULT_END_HOUR);
    [nine, twentyOne].forEach((t) => {
      if (t >= xMin && t <= xMax) {
        const x = ((t - xMin) / span) * cssWidth;
        ctx.strokeStyle = "rgba(59,130,246,.25)";
        ctx.setLineDash([4, 6]);
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(x + 0.5, 0);
        ctx.lineTo(x + 0.5, cssHeight);
        ctx.stroke();
        ctx.setLineDash([]);
      }
    });

    // NOW line
    const now = Date.now();
    if (now >= xMin && now <= xMax) {
      const nx = ((now - xMin) / span) * cssWidth;
      ctx.strokeStyle = THEME.now;
      ctx.setLineDash([6, 6]);
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(nx + 0.5, 0);
      ctx.lineTo(nx + 0.5, cssHeight);
      ctx.stroke();
      ctx.setLineDash([]);
    }

    // bars
    const pad = 3;
    items.forEach((it) => {
      const yTop = it.y * rowHeight + pad;
      const h = Math.max(12, rowHeight - pad * 2);
      const x0 = Math.max(xMin, it.sMs);
      const x1 = Math.min(xMax, it.eMs);
      if (x1 <= x0) return;
      const sx = ((x0 - xMin) / span) * cssWidth;
      const ex = ((x1 - xMin) / span) * cssWidth;
      const w = Math.max(2, ex - sx);

      const pal = PALETTE[it.palIdx % PALETTE.length];
      const grad = ctx.createLinearGradient(0, yTop, 0, yTop + h);
      grad.addColorStop(0, pal.top);
      grad.addColorStop(1, pal.bot);

      ctx.shadowColor = THEME.barShadow;
      ctx.shadowBlur = 10;
      ctx.shadowOffsetY = 3;
      roundRect(ctx, sx, yTop, w, h, 8, grad, pal.border);
      ctx.shadowBlur = 0;
      ctx.shadowOffsetY = 0;
    });

    // brush
    if (brush) {
      const xA = Math.min(brush.x0, brush.x1);
      const xB = Math.max(brush.x0, brush.x1);
      const cssX0 = ((xA - xMin) / span) * cssWidth;
      const cssX1 = ((xB - xMin) / span) * cssWidth;
      const w = Math.max(0, cssX1 - cssX0);

      ctx.fillStyle = THEME.brush;
      ctx.fillRect(cssX0, 0, w, cssHeight);

      ctx.strokeStyle = THEME.brushBorder;
      ctx.lineWidth = 2;
      ctx.strokeRect(cssX0 + 0.5, 0.5, Math.max(0, w - 1), cssHeight - 1);
    }
  }, [
    items,
    yLabels,
    yIsHeader,
    xMin,
    xMax,
    computedHeight,
    rowHeight,
    labelWidth,
    zebra,
    brush,
  ]);

  // hover
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const onMove = (ev: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      const x = ev.clientX - rect.left;
      const y = ev.clientY - rect.top;
      const cssWidth = rect.width;
      const span = xMax - xMin;
      const timeAt = xMin + (x / cssWidth) * span;
      const row = Math.floor(y / rowHeight);
      const hit = items.find(
        (it) => it.y === row && timeAt >= it.sMs && timeAt <= it.eMs
      );
      setHover({ x: ev.clientX, y: ev.clientY, item: hit || undefined });
    };
    const onLeave = () => setHover(null);
    canvas.addEventListener("mousemove", onMove);
    canvas.addEventListener("mouseleave", onLeave);
    return () => {
      canvas.removeEventListener("mousemove", onMove);
      canvas.removeEventListener("mouseleave", onLeave);
    };
  }, [items, xMin, xMax, rowHeight]);

  // mouse: pan / brush
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const down = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      const xPct = (e.clientX - rect.left) / rect.width;
      if (selectMode) {
        const span = xMax - xMin;
        const at = xMin + xPct * span;
        setBrush({ x0: at, x1: at });
        return;
      }
      dragging.current = true;
      dragStartX.current = e.clientX;
      dragStartMin.current = xMin;
      dragStartMax.current = xMax;
    };

    const move = (e: MouseEvent) => {
      if (selectMode && brush) {
        const rect = canvas.getBoundingClientRect();
        const xPct = (e.clientX - rect.left) / rect.width;
        const span = xMax - xMin;
        const at = xMin + xPct * span;
        setBrush((b) => (b ? { ...b, x1: at } : b));
        return;
      }
      if (!dragging.current) return;
      const rect = canvas.getBoundingClientRect();
      const dxPx = e.clientX - dragStartX.current;
      const span = dragStartMax.current - dragStartMin.current;
      const shift = -dxPx * (span / rect.width);
      setXMin(dragStartMin.current + shift);
      setXMax(dragStartMax.current + shift);
    };

    const up = () => {
      if (selectMode && brush) {
        const a = Math.min(brush.x0, brush.x1);
        const b = Math.max(brush.x0, brush.x1);
        if (b - a > 60_000) {
          setXMin(a);
          setXMax(b);
          onApplyDateRange?.(
            dateToInput(startOfDay(a)),
            dateToInput(startOfDay(b))
          );
        }
        setBrush(null);
      }
      dragging.current = false;
    };

    canvas.addEventListener("mousedown", down);
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    return () => {
      canvas.removeEventListener("mousedown", down);
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
    };
  }, [xMin, xMax, selectMode, brush, onApplyDateRange]);

  // wheel zoom/pan
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const rect = canvas.getBoundingClientRect();
      const mx = (e.clientX - rect.left) / rect.width;
      const span = xMax - xMin;
      const center = xMin + span * mx;
      const isZoom = e.ctrlKey || e.metaKey;
      if (isZoom) {
        const factor = Math.exp((e.deltaY / 200) * 0.2);
        const k = 1 / factor;
        const newMin = center - (center - xMin) * k;
        const newMax = center + (xMax - center) * k;
        const minSpan = 60_000;
        if (newMax - newMin > minSpan) {
          setXMin(newMin);
          setXMax(newMax);
        }
        return;
      }
      const dx =
        (Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY) || 0;
      const shift = dx * (span / rect.width);
      setXMin((v) => v + shift);
      setXMax((v) => v + shift);
    };
    canvas.addEventListener("wheel", onWheel, { passive: false });
    return () => canvas.removeEventListener("wheel", onWheel as any);
  }, [xMin, xMax]);

  // tick
  useEffect(() => {
    const id = setInterval(() => setXMin((v) => v), 1000);
    return () => clearInterval(id);
  }, []);

  // controls
  const zoomBy = (factor: number) => {
    const c = (xMin + xMax) / 2;
    const half = ((xMax - xMin) * factor) / 2;
    setXMin(c - half);
    setXMax(c + half);
  };
  const panBy = (ratio: number) => {
    const span = xMax - xMin;
    const shift = span * ratio;
    setXMin(xMin + shift);
    setXMax(xMax + shift);
  };
  const todayRange = () => {
    const d = Date.now();
    const s = atHour(d, DEFAULT_START_HOUR);
    const e = atHour(d, DEFAULT_END_HOUR);
    setXMin(s);
    setXMax(e);
    onApplyDateRange?.(dateToInput(s), dateToInput(e));
  };
  const toggleAll = (fold: boolean) => {
    const map: Record<string, boolean> = {};
    blocks.forEach((b) => (map[b.user] = fold));
    setCollapsed(map);
  };

  // ---------- Render ----------
  return (
    <Wrap ref={wrapRef}>
      <TopBar>
        {/* 좌측: 날짜 범위 (Title 제거 완료) */}
        <LeftArea>
          <label className="date-label">Start</label>
          <DateInputEl
            type="date"
            value={startStr}
            onChange={(e) => setStartStr(e.target.value)}
          />
          <label className="date-label">End</label>
          <DateInputEl
            type="date"
            value={endStr}
            onChange={(e) => setEndStr(e.target.value)}
          />
          <Button onClick={applyRange} variant="secondary">
            Apply
          </Button>
          <DividerV />
          <Button onClick={todayRange} variant="ghost">
            Today
          </Button>
          <Button onClick={() => setSelectMode((v) => !v)} active={selectMode}>
            {selectMode ? "✓ Range" : "Range"}
          </Button>
        </LeftArea>

        {/* 우측: 검색 + Menu 버튼 */}
        <RightArea>
          <Input
            type="text"
            placeholder="Filter by user / instance"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />

          <MenuWrap>
            <MenuBtn
              className="icon-menu"
              title="Menu"
              onClick={() => setMenuOpen((v) => !v)}
            />
            {menuOpen && (
              <MenuPanel role="menu" onMouseLeave={() => setMenuOpen(false)}>
                <MenuItem
                  role="menuitem"
                  onClick={() => {
                    zoomBy(0.8);
                    setMenuOpen(false);
                  }}
                >
                  ➕ Zoom In
                </MenuItem>
                <MenuItem
                  role="menuitem"
                  onClick={() => {
                    zoomBy(1.25);
                    setMenuOpen(false);
                  }}
                >
                  ➖ Zoom Out
                </MenuItem>
                <MenuItem
                  role="menuitem"
                  onClick={() => {
                    panBy(-0.2);
                    setMenuOpen(false);
                  }}
                >
                  ◀ Move
                </MenuItem>
                <MenuItem
                  role="menuitem"
                  onClick={() => {
                    panBy(0.2);
                    setMenuOpen(false);
                  }}
                >
                  ▶ Move
                </MenuItem>
                <MenuItem
                  role="menuitem"
                  onClick={() => {
                    toggleAll(true);
                    setMenuOpen(false);
                  }}
                >
                  ▦ Collapse All
                </MenuItem>
                <MenuItem
                  role="menuitem"
                  onClick={() => {
                    toggleAll(false);
                    setMenuOpen(false);
                  }}
                >
                  ▤ Expand All
                </MenuItem>
              </MenuPanel>
            )}
          </MenuWrap>
        </RightArea>
      </TopBar>

      {/* 본문 */}
      <div style={{ display: "flex", height: computedHeight }}>
        <LabelsPane style={{ width: labelWidth }}>
          {yLabels.map((label, i) => {
            const isH = yIsHeader[i];
            const name = label.replace(/^👤\s*/, "");
            return (
              <div
                key={i}
                onClick={() =>
                  isH && setCollapsed((m) => ({ ...m, [name]: !m[name] }))
                }
                title={isH ? "Click to toggle" : undefined}
                className={`row ${isH ? "head" : "item"} ${
                  i % 2 ? "odd" : "even"
                }`}
                style={{ height: rowHeight, lineHeight: `${rowHeight}px` }}
              >
                {isH ? (collapsed[name] ? "▸" : "▾") : ""} {label}
              </div>
            );
          })}
        </LabelsPane>

        <div style={{ position: "relative", flex: 1, overflow: "hidden" }}>
          <canvas
            ref={canvasRef}
            style={{
              display: "block",
              cursor: selectMode ? "crosshair" : "grab",
            }}
          />

          {/* Hover tooltip */}
          {hover?.item && (
            <div
              style={{
                position: "fixed",
                left: hover.x + 12,
                top: hover.y + 12,
                background: "#0b1220",
                color: "#fff",
                padding: "10px 12px",
                borderRadius: 12,
                boxShadow: "0 10px 30px rgba(2,6,23,.25)",
                fontSize: 12,
                pointerEvents: "none",
                zIndex: 30,
                border: "1px solid rgba(255,255,255,.08)",
              }}
            >
              <div style={{ fontWeight: 700, marginBottom: 4 }}>
                {hover.item.instance}{" "}
                <span style={{ color: "#94a3b8" }}>({hover.item.user})</span>
              </div>
              <div>
                {fmtHMst(hover.item.sMs)} ~ {fmtHMst(hover.item.eMs)}
              </div>
              <div>
                Duration{" "}
                <b>
                  {fmtDur(
                    Math.round(hover.item.sMs / 1000),
                    Math.round(hover.item.eMs / 1000)
                  )}
                </b>{" "}
                • {hover.item.samples} samples
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 차트 아래 설명 */}
      <HintBar>
        <Pill>Ctrl/⌘ + Wheel = Zoom</Pill>
        <Pill>Wheel = Move</Pill>
        <Pill>B = Range Select</Pill>
      </HintBar>
    </Wrap>
  );
};

export default GanttTimelineCanvas;
